import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { RecordModel } from 'pocketbase'
import type {
  ActivityEntry,
  Announcement,
  AppAlert,
  AppAlertKind,
  Billing,
  Candidate,
  LiveVouchEvent,
  PlanId,
  Recruiter,
  TeamMember,
} from './types'
import {
  pb,
  onAuthExpired,
  RECRUITERS,
  DEFAULT_PREFS,
  initialsOf,
  toActivity,
  toAnnouncement,
  toBilling,
  toCandidate,
  toRecruiter,
  toTeam,
  toVouch,
} from './pb'
import { eligible as eligibleAnnouncements, loadDismissed, saveDismissed } from './announcements'
import { fetchRiskFlags } from './riskFlagsApi'
import { checkpointLabel } from './candidate'
import { planById } from './constants'
import { loadAlerts, saveAlerts } from './alert'
import { enrollPasskey as apiEnrollPasskey, loginWithPasskey as apiLoginWithPasskey } from './passkey'
import {
  subscribe as apiSubscribe,
  cancelSubscription as apiCancelSubscription,
  resumeSubscription as apiResumeSubscription,
  createSetupIntent as apiCreateSetupIntent,
  setDefaultPaymentMethod as apiSetDefaultPaymentMethod,
  type SubscribeResult,
} from './billingApi'
import {
  inviteMember as apiInviteMember,
  resendInvite as apiResendInvite,
  changeRole as apiChangeRole,
  revokeMember as apiRevokeMember,
  acceptInvite as apiAcceptInvite,
  syncProfile as apiSyncProfile,
} from './teamApi'
import { sendVerifyRequests as apiSendVerifyRequests } from './verifyApi'
import {
  requestLiveVouchPush as apiRequestLiveVouchPush,
  unlockCandidate as apiUnlockCandidate,
  confirmLiveVouchPush as apiConfirmLiveVouch,
  checkResumeHash as apiCheckResumeHash,
  type ResumeCheckResult,
} from './liveVouchApi'
import { registerAccount } from './accountApi'

// ─────────────────────────────────────────────────────────────────────────
// Console store, backed by PocketBase. Auth and all data are real now:
//   - the session comes from pb.authStore (persisted in localStorage)
//   - candidates / activity / team / billing are fetched on sign-in
//   - mutations update local state optimistically for an instant-feeling UI,
//     then persist to PocketBase in the background (errors are logged; the
//     authoritative state is restored on the next load).
// ─────────────────────────────────────────────────────────────────────────

// A PocketBase-valid record id (15 chars, [a-z0-9]). We mint ids client-side for
// the create-based mutations (activity, team invites) and pass them straight to
// PocketBase, so the realtime `create` event echoing our own write carries the
// SAME id — `upsertSorted` then replaces in place instead of adding a duplicate.
const PB_ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789'
function genPbId(): string {
  const bytes = new Uint8Array(15)
  crypto.getRandomValues(bytes)
  let id = ''
  for (let i = 0; i < 15; i++) id += PB_ID_ALPHABET[bytes[i] % PB_ID_ALPHABET.length]
  return id
}

// Insert-or-replace `rec` by id, then re-sort. Used by the realtime handlers so
// an event is idempotent whether it's the first sighting of a record (insert),
// an update, or the echo of our own optimistic write (replace by matching id).
function upsertSorted<T extends { id: string }>(list: T[], rec: T, cmp: (a: T, b: T) => number): T[] {
  return [...list.filter((x) => x.id !== rec.id), rec].sort(cmp)
}

// Sort comparators matching the initial getFullList() orders, so realtime
// upserts keep each list in the same order the first load produced.
const byAddedAtDesc = (a: Candidate, b: Candidate) => (a.addedAt < b.addedAt ? 1 : a.addedAt > b.addedAt ? -1 : 0)
const byAtDesc = (a: ActivityEntry, b: ActivityEntry) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0)

// Detect a positive transition worth alerting on by diffing a realtime
// candidate update against the copy we already hold. Returns null unless the
// candidate *crossed into* a completed state — so we never alert on the initial
// load (no prior record) or on a no-op echo of our own optimistic write (the
// optimistic update already moved the state before the echo arrives).
function detectAlert(before: Candidate | undefined, after: Candidate): AppAlertKind | null {
  if (!before) return null
  // Diff on confirmation *count*, not the status transition: Live Vouch is now
  // repeatable (§12.1), so a 2nd+ re-confirm keeps status 'confirmed' the whole
  // time — keying on none→confirmed would silently drop every repeat alert.
  const beforeCount = before.liveVouch.history?.length ?? (before.liveVouch.status === 'confirmed' ? 1 : 0)
  const afterCount = after.liveVouch.history?.length ?? (after.liveVouch.status === 'confirmed' ? 1 : 0)
  if (afterCount > beforeCount) return 'live_vouch'
  // A recruiter-requested self-verify completing is the most specific signal — it
  // answers a request this recruiter sent, so it wins over the generic proof alert.
  if (before.verifyRequest !== 'completed' && after.verifyRequest === 'completed') return 'verify_completed'
  // Crossing into a higher trust tier (e.g. the candidate verified gov-ID in the
  // iOS app). Tier strictly increases as proofs land, so `>` is a real promotion.
  if (after.tier > before.tier) return 'tier_up'
  // A new discrete proof appeared on the ladder without a tier change (e.g. a
  // second tier-1 signal). Count only *present* proofs so we ignore catalogue rows.
  const presentBefore = before.signals.filter((s) => s.present).length
  const presentAfter = after.signals.filter((s) => s.present).length
  if (presentAfter > presentBefore) return 'signal_verified'
  return null
}

// Placeholders used before sign-in / before the first load resolves, so screens
// never see null. Real values replace them as soon as data arrives.
const EMPTY_RECRUITER: Recruiter = {
  name: '', email: '', company: '', domain: '', role: '', seatRole: 'recruiter', initials: '', avatarHue: 268,
  prefs: { ...DEFAULT_PREFS }, isStaff: false,
}
const DEFAULT_BILLING: Billing = {
  planId: 'free', used: 0, allowance: 0, renewsOn: '', seatsUsed: 0,
  card: { brand: '', last4: '', exp: '' }, invoices: [],
}

interface Session {
  recruiter: Recruiter
  authed: boolean
}

interface RegisterInput {
  email: string
  password: string
  role: string
}

interface StoreValue {
  session: Session
  candidates: Candidate[]
  activity: ActivityEntry[]
  team: TeamMember[]
  billing: Billing
  // derived
  allowanceUsed: number
  allowanceTotal: number
  // auth
  login: (email: string, password: string) => Promise<void>
  register: (input: RegisterInput) => Promise<void>
  logout: () => void
  // profile (edit name / company / role / console preferences)
  updateRecruiter: (
    patch: Partial<Pick<Recruiter, 'name' | 'company' | 'role' | 'prefs'>>,
  ) => Promise<void>
  // passkeys (WebAuthn, verified by the api sidecar)
  enrollPasskey: () => Promise<void>
  loginWithPasskey: () => Promise<void>
  // accept a team invite — sets a password, mints the recruiter login, and
  // adopts the returned session (the /accept-invite page; no prior auth).
  acceptInvite: (token: string, name: string, password: string) => Promise<void>
  // candidate actions
  unlock: (id: string) => void
  requestLiveVouch: (id: string, checkpoint?: string) => void
  confirmLiveVouch: (id: string, opts?: { checkpoint?: string; deviceKeyB64?: string }) => void
  /** Check a résumé the recruiter received against the candidate's device-signed
   *  fingerprint. `hash` is the file's SHA-256, computed in the recruiter's browser
   *  (the file itself is never sent). Throws on a permission/network error. */
  checkResume: (id: string, hash: string) => Promise<ResumeCheckResult>
  logView: (id: string) => void
  // Load the trust-chain vouches a candidate has *received* (§12.4) and attach
  // them to that candidate's `vouchesIn`. Called when a profile opens; best-effort.
  loadVouches: (id: string) => Promise<void>
  // Ask candidates to self-verify: the sidecar mints a token, sets the candidate
  // pending, and emails each a secure self-verify link. Resolves with how many
  // requests were sent and how many invite emails actually went out (delivery is
  // best-effort — a candidate can still be re-sent). Throws on a permission/network
  // error so the screen can surface it and roll back the optimistic flip.
  sendVerifyRequests: (ids: string[]) => Promise<{ sent: number; emailed: number }>

  // team (owner/admin-only; persisted through the api sidecar, which emails the
  // invite and mints/disables the linked recruiter login). Realtime team events
  // reconcile the roster; these throw on a permission/network error so the UI
  // can surface it and roll back the optimistic change.
  inviteMember: (name: string, email: string, role: TeamMember['role']) => Promise<{ emailDelivered: boolean }>
  removeMember: (id: string) => Promise<void>
  changeMemberRole: (id: string, role: TeamMember['role']) => Promise<void>
  resendInvite: (id: string) => Promise<void>
  // billing (Stripe-backed; the webhook + realtime billing subscription are the
  // source of truth, so these kick Stripe and let the record update stream in)
  subscribeToPlan: (planId: PlanId) => Promise<SubscribeResult>
  cancelPlan: () => Promise<void>
  resumePlan: () => Promise<void>
  beginCardUpdate: () => Promise<string>
  finishCardUpdate: (paymentMethodId: string) => Promise<void>
  // live alerts (in-app notifications, fed by realtime candidate events)
  alerts: AppAlert[]
  unreadAlerts: number
  markAlertsRead: () => void
  dismissAlert: (id: string) => void
  // platform announcements eligible to show this recruiter as a banner, plus a
  // per-recruiter dismiss (persisted to localStorage)
  bannerAnnouncements: Announcement[]
  dismissAnnouncement: (id: string) => void
}

const StoreContext = createContext<StoreValue | null>(null)

export function StoreProvider({ children }: { children: ReactNode }) {
  const [authed, setAuthed] = useState(pb.authStore.isValid)
  const [recruiter, setRecruiter] = useState<Recruiter>(
    () => toRecruiter(pb.authStore.record) ?? EMPTY_RECRUITER,
  )
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [activity, setActivity] = useState<ActivityEntry[]>([])
  const [team, setTeam] = useState<TeamMember[]>([])
  const [billing, setBilling] = useState<Billing>(DEFAULT_BILLING)
  const [alerts, setAlerts] = useState<AppAlert[]>([])
  // Published platform announcements (raw) + the per-recruiter set of banner ids
  // they've dismissed. Eligibility (audience/expiry/channel) is derived below.
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [dismissedAnn, setDismissedAnn] = useState<Set<string>>(new Set())

  // Refs read by the realtime candidate handler (which is created once per
  // sign-in and would otherwise close over stale state): the last committed
  // candidate list, to diff transitions against, and the live value of the
  // `liveAlerts` preference, so toggling it takes effect without resubscribing.
  const candidatesRef = useRef<Candidate[]>(candidates)
  candidatesRef.current = candidates
  const liveAlertsRef = useRef(recruiter.prefs.liveAlerts)
  liveAlertsRef.current = recruiter.prefs.liveAlerts

  // Negative risk flags (shared-device, etc.) live on a HIDDEN candidates field, so
  // they don't ride the realtime feed — they're fetched once on load from the
  // recruiter-authed projection and kept here, then re-attached to every candidate
  // (including ones rebuilt by realtime, which would otherwise drop the flag).
  const riskFlagsRef = useRef<Record<string, import('./types').RiskFlag[]>>({})
  const withFlags = useCallback((c: Candidate): Candidate => {
    const f = riskFlagsRef.current[c.id]
    return f ? { ...c, riskFlags: f } : c
  }, [])

  const pushAlert = useCallback((kind: AppAlertKind, c: Candidate) => {
    const alert: AppAlert = {
      id: genPbId(),
      kind,
      candidateId: c.id,
      candidateName: c.name,
      at: new Date().toISOString(),
      read: false,
    }
    setAlerts((prev) => [alert, ...prev].slice(0, 30))
  }, [])

  const markAlertsRead = useCallback(() => {
    setAlerts((prev) => (prev.some((a) => !a.read) ? prev.map((a) => ({ ...a, read: true })) : prev))
  }, [])

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id))
  }, [])

  // Keep our session state in lockstep with the PocketBase auth store. Fires
  // immediately to sync the initial value, then on every login/logout/refresh.
  useEffect(() => {
    return pb.authStore.onChange(() => {
      setAuthed(pb.authStore.isValid)
      setRecruiter(toRecruiter(pb.authStore.record) ?? EMPTY_RECRUITER)
    }, true)
  }, [])

  // onChange (above) doesn't fire when a token simply expires while the tab sits
  // idle, so poll its validity (a cheap local exp check) and re-check on
  // focus/visibility. Once it's expired, clear it → onChange flips authed false →
  // RequireAuth redirects to /login, instead of pages quietly starting to 401.
  useEffect(() => {
    const check = () => {
      if (pb.authStore.token && !pb.authStore.isValid) onAuthExpired()
    }
    check()
    const id = window.setInterval(check, 30_000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') check()
    }
    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  // Load (or clear) the console data whenever the auth state flips.
  useEffect(() => {
    if (!authed) {
      setCandidates([])
      setActivity([])
      setTeam([])
      setBilling(DEFAULT_BILLING)
      setAlerts([])
      setAnnouncements([])
      setDismissedAnn(new Set())
      return
    }
    // Rehydrate the bell from the per-recruiter persisted history so a reload (or
    // reopening the tab) doesn't drop alerts the recruiter hasn't acted on yet.
    setAlerts(loadAlerts(pb.authStore.record?.id))
    setDismissedAnn(loadDismissed(pb.authStore.record?.id))
    let cancelled = false
    ;(async () => {
      try {
        const [cs, acts, tm] = await Promise.all([
          pb.collection('candidates').getFullList({ sort: '-addedAt' }),
          pb.collection('activity').getFullList({ sort: '-at' }),
          pb.collection('team_members').getFullList({ sort: 'created' }),
        ])
        if (cancelled) return
        setCandidates(cs.map(toCandidate))
        setActivity(acts.map(toActivity))
        setTeam(tm.map(toTeam))

        // Negative risk flags ride a separate recruiter-authed projection (the
        // source field is hidden). Best-effort: a failure just leaves the pool
        // unflagged rather than blocking the console load.
        try {
          const flags = await fetchRiskFlags(pb.authStore.token)
          if (cancelled) return
          riskFlagsRef.current = flags
          setCandidates((prev) => prev.map(withFlags))
        } catch (err) {
          console.error('risk-flags load failed:', err)
        }

        // Multi-tenant: load only THIS account's billing record (keyed by the
        // accountId on the signed-in recruiter), not "the one billing record".
        const accountId = pb.authStore.record?.accountId || ''
        const bills = accountId
          ? await pb.collection('billing').getList(1, 1, { filter: `accountId = "${accountId}"` })
          : { items: [] as RecordModel[] }
        if (cancelled) return
        if (bills.items[0]) {
          setBilling(toBilling(bills.items[0]))
        }

        // Published platform announcements (the collection rule already restricts
        // reads to published rows). Powers the in-console banner + the bell.
        const anns = await pb.collection('announcements').getFullList({ sort: '-publishedAt' })
        if (cancelled) return
        setAnnouncements(anns.map(toAnnouncement))

        // Now that the initial snapshot is in, keep the console live. Realtime
        // authorises each event against the same per-collection viewRule the
        // initial load used — now tenant-scoped (1700000280), so a recruiter only
        // ever sees their OWN account's rows. Handlers upsert/remove by id, which
        // makes them idempotent against our own optimistic writes (client-minted
        // ids match the echo) and reflects external changes — notably a candidate
        // completing verification in the iOS app, or another recruiter's actions.
        await Promise.all([
          pb.collection('candidates').subscribe('*', (e) => {
            if (e.action === 'delete') {
              setCandidates((prev) => prev.filter((c) => c.id !== e.record.id))
              return
            }
            const incoming = withFlags(toCandidate(e.record))
            // Diff against committed state (not the updater's `prev`) so the alert
            // side effect runs exactly once, never twice under StrictMode.
            const before = candidatesRef.current.find((c) => c.id === incoming.id)
            const kind = detectAlert(before, incoming)
            // Scope the alert to the inviting recruiter. An invited candidate carries
            // `invitedByRecruiterId`; only that recruiter should hear the bell when
            // their invite verifies. Rows with no inviter (the shared pool, app
            // self-publish) keep today's behavior: every console alerts.
            const mine =
              !incoming.invitedByRecruiterId ||
              incoming.invitedByRecruiterId === pb.authStore.record?.id
            if (kind && mine && liveAlertsRef.current) pushAlert(kind, incoming)
            setCandidates((prev) => upsertSorted(prev, incoming, byAddedAtDesc))
          }),
          pb.collection('activity').subscribe('*', (e) => {
            setActivity((prev) =>
              e.action === 'delete'
                ? prev.filter((a) => a.id !== e.record.id)
                : upsertSorted(prev, toActivity(e.record), byAtDesc),
            )
          }),
          pb.collection('team_members').subscribe('*', (e) => {
            setTeam((prev) =>
              e.action === 'delete'
                ? prev.filter((m) => m.id !== e.record.id)
                : // team renders in creation order; new rows append, existing
                  // rows replace in place (stable sort with a no-op comparator).
                  upsertSorted(prev, toTeam(e.record), () => 0),
            )
          }),
          pb.collection('billing').subscribe('*', (e) => {
            if (e.action === 'delete') return
            // Multi-tenant: ignore other accounts' billing events (the realtime
            // feed is org-wide; only adopt our own account's record).
            if (accountId && e.record.accountId !== accountId) return
            setBilling(toBilling(e.record))
          }),
          // Live announcements: a staff publish streams in so the banner/bell
          // appear without a reload. The collection rule only delivers published
          // rows; an unpublish/delete drops it from the list.
          pb.collection('announcements').subscribe('*', (e) => {
            setAnnouncements((prev) =>
              e.action === 'delete'
                ? prev.filter((a) => a.id !== e.record.id)
                : [toAnnouncement(e.record), ...prev.filter((a) => a.id !== e.record.id)],
            )
          }),
        ])
      } catch (err) {
        if (!cancelled) console.error('Failed to load console data', err)
      }
    })()
    return () => {
      cancelled = true
      // Drop every console subscription; reopened on the next sign-in.
      for (const c of ['candidates', 'activity', 'team_members', 'billing', 'announcements']) {
        pb.collection(c).unsubscribe()
      }
    }
    // pushAlert and withFlags are stable useCallbacks; listing them satisfies the
    // linter without causing the load/subscribe effect to re-run.
  }, [authed, pushAlert, withFlags])

  // Mirror the bell to localStorage whenever it changes, keyed by recruiter. Only
  // while authed: logout flips `authed` false and clears `alerts` to [], and we
  // must not let that empty write wipe the persisted history we want to restore.
  useEffect(() => {
    if (!authed) return
    saveAlerts(pb.authStore.record?.id, alerts)
  }, [alerts, authed])

  // Announcements eligible to BANNER for this recruiter (audience + not expired +
  // not dismissed). Recomputed when the list, the recruiter's account/plan, or
  // the dismissed set changes.
  const bannerAnnouncements = useMemo(
    () =>
      eligibleAnnouncements(announcements, { accountId: recruiter.accountId, planId: billing.planId }, 'banner').filter(
        (a) => !dismissedAnn.has(a.id),
      ),
    [announcements, recruiter.accountId, billing.planId, dismissedAnn],
  )

  const dismissAnnouncement = useCallback((id: string) => {
    setDismissedAnn((prev) => {
      const next = new Set(prev).add(id)
      saveDismissed(pb.authStore.record?.id, next)
      return next
    })
  }, [])

  // Mirror BELL-channel announcements into the alert bell, once each. Keyed by
  // `ann_<id>` so a given announcement is added at most once and dedups against
  // the persisted history (it survives reloads like any other bell alert).
  useEffect(() => {
    if (!authed) return
    const bell = eligibleAnnouncements(
      announcements,
      { accountId: recruiter.accountId, planId: billing.planId },
      'bell',
    )
    if (!bell.length) return
    setAlerts((prev) => {
      const have = new Set(prev.map((a) => a.id))
      const add: AppAlert[] = bell
        .filter((a) => !have.has(`ann_${a.id}`))
        .map((a) => ({
          id: `ann_${a.id}`,
          kind: 'announcement',
          candidateId: '',
          candidateName: a.title,
          title: a.title,
          at: a.publishedAt || new Date().toISOString(),
          read: false,
        }))
      return add.length ? [...add, ...prev].slice(0, 30) : prev
    })
  }, [authed, announcements, recruiter.accountId, billing.planId])

  // ── auth ──────────────────────────────────────────────────────────────────
  const login = useCallback(async (email: string, password: string) => {
    await pb.collection(RECRUITERS).authWithPassword(email.trim().toLowerCase(), password)
    // onChange() updates `authed` + `recruiter`, which triggers the data load.
  }, [])

  // Owner self-signup. Multi-tenant: the sidecar creates the login, the org
  // `accounts` record, and a free `billing` record atomically (the browser can't
  // write those superuser-only collections), then returns a session we adopt —
  // onChange() flips `authed` and triggers the data load (same as acceptInvite).
  const register = useCallback(async ({ email, password, role }: RegisterInput) => {
    const e = email.trim().toLowerCase()
    const { token, record } = await registerAccount({ email: e, password, role })
    pb.authStore.save(token, record)
  }, [])

  const logout = useCallback(() => pb.authStore.clear(), [])

  // Update the signed-in recruiter's own profile/preferences. Optimistic local
  // update for an instant UI, then persist to PocketBase and adopt the saved
  // record into the auth store — onChange() re-syncs every consumer from the
  // authoritative copy. `initials` is re-derived whenever the name changes.
  const updateRecruiter = useCallback(
    async (patch: Partial<Pick<Recruiter, 'name' | 'company' | 'role' | 'prefs'>>) => {
      const id = pb.authStore.record?.id
      setRecruiter((prev) => {
        const next = { ...prev, ...patch }
        if (patch.name !== undefined) next.initials = initialsOf(patch.name || prev.email)
        return next
      })
      if (!id) return
      const data: Record<string, unknown> = { ...patch }
      if (patch.name !== undefined) data.initials = initialsOf(patch.name || recruiter.email)
      try {
        const rec = await pb.collection(RECRUITERS).update(id, data)
        pb.authStore.save(pb.authStore.token, rec)
        // Keep this recruiter's own team seat in lockstep with the profile when
        // the display name changed. The sidecar updates only the caller's seat
        // (never its role); the realtime team subscription streams it back in.
        if (patch.name !== undefined) {
          apiSyncProfile(pb.authStore.token).catch((err) => console.error('seat sync failed', err))
        }
      } catch (err) {
        console.error('profile persist failed', err)
        throw err
      }
    },
    [recruiter.email],
  )

  // Enrol a passkey for the signed-in recruiter (forwards the current session
  // token so the sidecar can attribute it).
  const enrollPasskey = useCallback(async () => {
    await apiEnrollPasskey(pb.authStore.token)
  }, [])

  // Usernameless passkey sign-in: the sidecar mints a session, which we adopt.
  // onChange() then flips `authed` and triggers the data load.
  const loginWithPasskey = useCallback(async () => {
    const { token, record } = await apiLoginWithPasskey()
    pb.authStore.save(token, record)
  }, [])

  // Accept a team invite: the sidecar verifies the token, creates the recruiter
  // login with a password, activates the seat, and returns a session we adopt —
  // onChange() then flips `authed` and triggers the data load (same as passkey).
  const acceptInvite = useCallback(async (token: string, name: string, password: string) => {
    const { token: sessionToken, record } = await apiAcceptInvite({ token, name, password })
    pb.authStore.save(sessionToken, record)
  }, [])

  // ── activity (optimistic prepend + background persist) ────────────────────
  const pushActivity = useCallback(
    (entry: Omit<ActivityEntry, 'id' | 'at' | 'actor'> & { at?: string; actor?: string }) => {
      const at = entry.at ?? new Date().toISOString()
      const actor = entry.actor ?? recruiter.name
      // Mint the id client-side so the realtime echo of this create dedups
      // against the optimistic row instead of appearing twice.
      const optimistic: ActivityEntry = { id: genPbId(), at, actor, ...entry }
      setActivity((prev) => [optimistic, ...prev])
      pb.collection('activity')
        .create({
          id: optimistic.id,
          kind: optimistic.kind,
          at,
          actor,
          candidateId: optimistic.candidateId ?? '',
          candidateName: optimistic.candidateName ?? '',
          detail: optimistic.detail ?? '',
        })
        .catch((err) => console.error('activity persist failed', err))
    },
    [recruiter.name],
  )

  // ── candidate actions ─────────────────────────────────────────────────────
  const updateCandidate = useCallback((id: string, patch: (c: Candidate) => Candidate) => {
    setCandidates((prev) => prev.map((c) => (c.id === id ? patch(c) : c)))
  }, [])

  const loadVouches = useCallback(
    async (id: string) => {
      try {
        const rows = await pb.collection('vouches').getFullList({
          filter: `subjectId = "${id}"`,
          sort: '-createdAt',
        })
        const vouchesIn = rows.map(toVouch)
        updateCandidate(id, (x) => ({ ...x, vouchesIn }))
      } catch (err) {
        // Best-effort: a missing collection or network blip just leaves the
        // trust-chain card empty; never block the profile on it.
        console.error('vouches load failed', err)
      }
    },
    [updateCandidate],
  )

  const unlock = useCallback(
    (id: string) => {
      const c = candidates.find((x) => x.id === id)
      if (!c || c.unlocked) return
      // Client-side cap check is only UX — the disabled button. The sidecar
      // re-derives the cap and meters the unlock server-side (candidates + billing
      // are sidecar-only now, 1700000280), so it can't be reset from devtools.
      const cap = planById(billing.planId).allowance
      if (cap != null && billing.used >= cap) return
      // Optimistic reveal for an instant UI; the realtime billing subscription
      // reconciles `used` to the authoritative server value.
      updateCandidate(id, (x) => ({ ...x, unlocked: true }))
      setBilling((b) => ({ ...b, used: b.used + 1 }))
      pushActivity({ kind: 'unlock', candidateId: id, candidateName: c.name })
      apiUnlockCandidate(id, pb.authStore.token).catch((err) => {
        console.error('unlock failed', err)
        // Roll back the optimistic reveal + meter bump on a permission/cap error.
        updateCandidate(id, (x) => ({ ...x, unlocked: false }))
        setBilling((b) => ({ ...b, used: Math.max(0, b.used - 1) }))
      })
    },
    [candidates, updateCandidate, pushActivity, billing.planId, billing.used],
  )

  const logView = useCallback(
    (id: string) => {
      const c = candidates.find((x) => x.id === id)
      if (!c) return
      pushActivity({ kind: 'view', candidateId: id, candidateName: c.name })
    },
    [candidates, pushActivity],
  )

  const requestLiveVouch = useCallback(
    (id: string, checkpoint?: string) => {
      const c = candidates.find((x) => x.id === id)
      if (!c) return
      // Preserve any prior history — marking "waiting" must not erase confirmed
      // events. The real confirmation (produced on the candidate's device and
      // mirrored onto this row by the backend) appends to this history; wiping it
      // here would make that append start from empty and drop the vouch trail.
      const liveVouch = { ...c.liveVouch, status: 'waiting' as const, requestedAt: new Date().toISOString() }
      updateCandidate(id, (x) => ({ ...x, liveVouch }))
      pushActivity({ kind: 'live_vouch_requested', candidateId: id, candidateName: c.name })
      // The sidecar persists the "waiting" state on the candidate row (the browser
      // can't write candidates anymore — 1700000280) AND mirrors the request onto
      // the candidate's app account so their iOS device wakes a Live Vouch
      // notification. Realtime echoes the authoritative row back.
      apiRequestLiveVouchPush(id, pb.authStore.token, checkpoint).catch((err) =>
        console.error('live vouch request failed', err),
      )
    },
    [candidates, updateCandidate, pushActivity],
  )

  const confirmLiveVouch = useCallback(
    (id: string, opts?: { checkpoint?: string; deviceKeyB64?: string }) => {
      const c = candidates.find((x) => x.id === id)
      if (!c) return
      const at = new Date().toISOString()
      const prior = c.liveVouch.history ?? []
      // In production the nonce, signature, and device key are produced by the
      // candidate's iOS app (signed in the Secure Enclave) and arrive with the
      // confirmation. The console demo synthesizes them; the device key defaults
      // to a stable per-candidate value so repeat confirmations read as the SAME
      // device — the normal, unflagged case (§12.2).
      const deviceKeyB64 = opts?.deviceKeyB64 ?? `dev_${c.id}`
      const event: LiveVouchEvent = {
        id: genPbId(),
        confirmedAt: at,
        nonce: genPbId(),
        deviceKeyB64,
        signatureB64: '',
        checkpoint: opts?.checkpoint,
      }
      const history = [...prior, event]
      const signals = c.signals.map((s) =>
        s.id === 'live_vouch' ? { ...s, present: true, verifiedAt: at.slice(0, 10) } : s,
      )
      const next: Pick<Candidate, 'liveVouch' | 'state' | 'tier' | 'signals'> = {
        liveVouch: { status: 'confirmed', confirmedAt: at, history },
        state: c.state === 'stale' ? 'live' : c.state,
        tier: 3,
        signals,
      }
      updateCandidate(id, (x) => ({ ...x, ...next }))
      pushActivity({
        kind: 'live_vouch_confirmed',
        candidateId: id,
        candidateName: c.name,
        detail: opts?.checkpoint ? `Face ID challenge passed · ${checkpointLabel(opts.checkpoint)}` : 'Face ID challenge passed',
      })
      // Device-consistency flag (§12.2): a confirmation from a device not seen on
      // any prior checkpoint is surfaced as a review flag — never a block, since
      // multi-device use is legitimate (§9).
      if (prior.length > 0 && !prior.some((e) => e.deviceKeyB64 === deviceKeyB64)) {
        pushActivity({
          kind: 'live_vouch_device_changed',
          candidateId: id,
          candidateName: c.name,
          detail: 'Confirmed from a new device — allowed per multi-device policy, review',
        })
      }
      // Persist through the sidecar (candidates are sidecar-only now, 1700000280).
      // The route confirms DEMO rows only — a real candidate confirms on their own
      // device — so roll the optimistic state back if it refuses.
      apiConfirmLiveVouch(id, pb.authStore.token, {
        checkpoint: opts?.checkpoint,
        deviceKeyB64: opts?.deviceKeyB64,
      }).catch((err) => {
        console.error('live vouch confirm failed', err)
        updateCandidate(id, () => c)
      })
    },
    [candidates, updateCandidate, pushActivity],
  )

  // Check a résumé the recruiter RECEIVED against the fingerprint the candidate
  // device-signed. `hash` is the SHA-256 of that file, computed in this browser —
  // the document never leaves it, mirroring how the candidate signed it. The verdict
  // is the server's (it also replays the candidate's signature); the result is
  // ephemeral and deliberately does NOT write back onto the candidate row, since it
  // describes the recruiter's copy, not the record.
  const checkResume = useCallback(
    async (id: string, hash: string): Promise<ResumeCheckResult> => {
      const c = candidates.find((x) => x.id === id)
      if (!c) throw new Error('Candidate not found.')
      const result = await apiCheckResumeHash(id, hash, pb.authStore.token)
      pushActivity({
        kind: 'resume_checked',
        candidateId: id,
        candidateName: c.name,
        detail:
          result.match === 'unverified'
            ? 'No signed résumé on file to check against'
            : result.match === 'mismatch'
              ? 'File did NOT match the signed fingerprint — review carefully'
              : result.signatureValid === false
                ? 'File matched, but the signature on record did not verify'
                : result.signatureValid === null
                  ? 'File matched the fingerprint on record (signature unverifiable)'
                  : 'File matched the candidate’s signed fingerprint',
      })
      return result
    },
    [candidates, pushActivity],
  )

  const sendVerifyRequests = useCallback(
    async (ids: string[]) => {
      const named = candidates.filter((c) => ids.includes(c.id))
      // Optimistic flip for an instant UI; the sidecar owns the authoritative
      // candidate write (minting the link token + setting pending) and the
      // realtime echo reconciles it. We don't persistCandidate here anymore.
      const prevById = new Map(named.map((c) => [c.id, c.verifyRequest]))
      setCandidates((prev) =>
        prev.map((c) => (ids.includes(c.id) ? { ...c, verifyRequest: 'pending' } : c)),
      )
      try {
        const { results } = await apiSendVerifyRequests(ids, pb.authStore.token)
        const ok = results.filter((r) => r.ok)
        // Log the recruiter-side "request sent" activity only for candidates the
        // sidecar actually queued (a missing id is skipped, not flipped).
        named
          .filter((c) => ok.some((r) => r.id === c.id))
          .forEach((c) =>
            pushActivity({
              kind: 'verify_sent',
              candidateId: c.id,
              candidateName: c.name,
              detail: 'Phone verification requested',
            }),
          )
        return { sent: ok.length, emailed: ok.filter((r) => r.emailDelivered).length }
      } catch (err) {
        // Roll the optimistic flip back to each candidate's prior status.
        setCandidates((prev) =>
          prev.map((c) => (prevById.has(c.id) ? { ...c, verifyRequest: prevById.get(c.id)! } : c)),
        )
        throw err
      }
    },
    [candidates, pushActivity],
  )

  // ── team ──────────────────────────────────────────────────────────────────
  // billing.seatsUsed is maintained server-side now: the invite/revoke ceremonies
  // recount this account's seats and write the count as superuser (team.mjs), since
  // the browser can no longer write billing (1700000280). The optimistic local
  // seat count below is just for an instant UI; realtime billing reconciles it.
  const inviteMember = useCallback(
    async (name: string, email: string, role: TeamMember['role']) => {
      const initials =
        name
          .split(' ')
          .map((p) => p[0])
          .join('')
          .slice(0, 2)
          .toUpperCase() || '??'
      const avatarHue = (team.length * 47) % 360
      // Client-minted id (same as activity) so the realtime create echo replaces
      // this optimistic row by id rather than adding a duplicate — no temp→real swap.
      const member: TeamMember = {
        id: genPbId(), name, email, role, status: 'invited', avatarHue, initials, lastActive: '—',
      }
      const seatsUsed = team.length + 1
      setTeam((prev) => [...prev, member])
      setBilling((b) => ({ ...b, seatsUsed }))
      try {
        // The sidecar creates the seat (with our id), mints an invite token, and
        // emails the accept link. Realtime delivers the authoritative row.
        const { emailDelivered } = await apiInviteMember(
          { id: member.id, name, email, role },
          pb.authStore.token,
        )
        return { emailDelivered }
      } catch (err) {
        // Roll the optimistic seat back so the roster stays truthful.
        setTeam((prev) => prev.filter((m) => m.id !== member.id))
        setBilling((b) => ({ ...b, seatsUsed: team.length }))
        throw err
      }
    },
    [team.length],
  )

  const removeMember = useCallback(
    async (id: string) => {
      const removed = team.find((m) => m.id === id)
      if (!removed) return
      const seatsUsed = team.length - 1
      setTeam((prev) => prev.filter((m) => m.id !== id))
      setBilling((b) => ({ ...b, seatsUsed }))
      try {
        // Sidecar deletes the seat, disables its login, and recounts seats onto
        // billing; realtime confirms both the roster and the seat count.
        await apiRevokeMember(id, pb.authStore.token)
      } catch (err) {
        // Restore the seat in its original position on failure.
        setTeam((prev) => (prev.some((m) => m.id === id) ? prev : [...prev, removed]))
        setBilling((b) => ({ ...b, seatsUsed: team.length }))
        throw err
      }
    },
    [team],
  )

  const changeMemberRole = useCallback(
    async (id: string, role: TeamMember['role']) => {
      const prevRole = team.find((m) => m.id === id)?.role
      if (!prevRole || prevRole === role) return
      setTeam((prev) => prev.map((m) => (m.id === id ? { ...m, role } : m)))
      try {
        await apiChangeRole(id, role, pb.authStore.token)
      } catch (err) {
        setTeam((prev) => prev.map((m) => (m.id === id ? { ...m, role: prevRole } : m)))
        throw err
      }
    },
    [team],
  )

  const resendInvite = useCallback(async (id: string) => {
    await apiResendInvite(id, pb.authStore.token)
  }, [])

  // ── billing (Stripe) ────────────────────────────────────────────────────────
  // These no longer write the billing record directly: each one kicks Stripe via
  // the api service, and the Stripe webhook writes the authoritative plan / card /
  // invoice state back onto the record — which streams into the UI through the
  // realtime `billing` subscription above. We deliberately don't optimistically
  // mutate local state, so what the recruiter sees always matches Stripe.

  // Start or switch a paid plan. Returns the server's decision: `updated` (an
  // existing subscription was re-priced, no card needed) or `requires_payment`
  // with a clientSecret the caller confirms with Stripe Elements.
  const subscribeToPlan = useCallback(
    (planId: PlanId) => apiSubscribe(planId, pb.authStore.token),
    [],
  )

  // Downgrade to Free — schedules cancellation at the end of the billing period
  // (no refund); the plan stays active until then.
  const cancelPlan = useCallback(async () => {
    await apiCancelSubscription(pb.authStore.token)
  }, [])

  // Undo a scheduled downgrade — keep the current plan.
  const resumePlan = useCallback(async () => {
    await apiResumeSubscription(pb.authStore.token)
  }, [])

  // "Update card", part 1: a SetupIntent client secret for Elements to confirm.
  const beginCardUpdate = useCallback(
    () => apiCreateSetupIntent(pb.authStore.token).then((r) => r.clientSecret),
    [],
  )

  // "Update card", part 2: adopt the freshly-saved card as the default.
  const finishCardUpdate = useCallback(async (paymentMethodId: string) => {
    await apiSetDefaultPaymentMethod(paymentMethodId, pb.authStore.token)
  }, [])

  const session = useMemo<Session>(() => ({ recruiter, authed }), [recruiter, authed])

  const value = useMemo<StoreValue>(
    () => ({
      session,
      candidates,
      activity,
      team,
      billing,
      // The real limit comes from the plan definition, not the stored number,
      // so it's always in sync with the current plan (unlimited → Infinity).
      allowanceUsed: billing.used,
      allowanceTotal: planById(billing.planId).allowance ?? Infinity,
      login,
      register,
      logout,
      updateRecruiter,
      enrollPasskey,
      loginWithPasskey,
      acceptInvite,
      unlock,
      requestLiveVouch,
      confirmLiveVouch,
      checkResume,
      logView,
      loadVouches,
      sendVerifyRequests,
      inviteMember,
      removeMember,
      changeMemberRole,
      resendInvite,
      subscribeToPlan,
      cancelPlan,
      resumePlan,
      beginCardUpdate,
      finishCardUpdate,
      alerts,
      unreadAlerts: alerts.reduce((n, a) => (a.read ? n : n + 1), 0),
      markAlertsRead,
      dismissAlert,
      bannerAnnouncements,
      dismissAnnouncement,
    }),
    [
      session,
      candidates,
      activity,
      team,
      billing,
      login,
      register,
      logout,
      updateRecruiter,
      enrollPasskey,
      loginWithPasskey,
      acceptInvite,
      unlock,
      requestLiveVouch,
      confirmLiveVouch,
      checkResume,
      logView,
      loadVouches,
      sendVerifyRequests,
      inviteMember,
      removeMember,
      changeMemberRole,
      resendInvite,
      subscribeToPlan,
      cancelPlan,
      resumePlan,
      beginCardUpdate,
      finishCardUpdate,
      alerts,
      markAlertsRead,
      dismissAlert,
      bannerAnnouncements,
      dismissAnnouncement,
    ],
  )

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useStore() {
  const ctx = useContext(StoreContext)
  if (!ctx) throw new Error('useStore must be used within StoreProvider')
  return ctx
}
